import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ThirdPartyConfigsSync as ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/configs-sync';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type EntityATI } from '@atlassian/search-client/common/types';
import { smartsRecommendedContainerQuery } from '@atlassian/search-client/container';
import { searchEntitiesQuery } from '@atlassian/search-client/entities';
import {
	type LoomSpacesByIdsQueryData,
	loomSpaceARIEntitiesQuery,
} from '@atlassian/search-client/loom/space';
import {
	type LoomSpacesSearchQueryData,
	loomSearchEntitiesQuery,
} from '@atlassian/search-client/loom/space-search';
import {
	confluenceDcSpaceFilterOptionsQuery,
	spaceARIEntitiesQuery,
	thirdPartySpaceFilterOptionsQuery,
} from '@atlassian/search-client/search-page';
import { confluenceSpacesQuery } from '@atlassian/search-client/space';
import { confluenceSpaceKeysToIdsQuery } from '@atlassian/search-client/space-keys-to-ids';
import { fetchOptionsAcrossSites } from '@atlassian/search-common/fetch-options-across-sites';
import { getCloudIdARIs } from '@atlassian/search-common/get-cloud-id-ari';
import { mergeAndDedupeOptions } from '@atlassian/search-common/merge-and-dedupe-options';
import { cleanExcerpt } from '@atlassian/search-common/search-result-utils';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';

import { DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI } from '../../../../../common/constants/default-cloud-graph-space-entity-ati';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	CreateSelectFilter,
	FetchedSelectFilterOption,
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { NounSubtypeKey } from '../../../../../common/constants/filters/universal-type';
import {
	is3pProductKey,
	ProductKeys,
	type ProductKeys1P,
} from '../../../../../common/constants/product-keys';
import { SearchExperience } from '../../../../../common/constants/search-experience';
import { getProducts3PFromConfigForFilter, notEmpty } from '../../../../../common/utils/filters';
import { isHello } from '../../../../../common/utils/is-hello';
import {
	MultiSelectFilter,
	createMultiSelectFilter,
} from '../../../../filters/multi-select-filter';
import { SingleSelectFilter } from '../../../../filters/single-select-filter';

type Space = {
	/** Space ARI */
	ari: string;
	/** Space key, e.g. "MICROS" */
	key: string;
	name: string | null;
	avatarUrl: string;
};

// TODO: Add Loom in 1P product when backend is ready
const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('space');

const getProducts3PFromConfig = () => products3PFromConfig;
const getFilterProducts = (): ProductKeys[] => [
	...new Set([
		...productKeys1P,
		...getProducts3PFromConfig(),
		...(fg('loom-rovo-search') ? [ProductKeys.Loom] : []),
	]),
];

const LOOKUP_ENTITIES: EntityATI[] = ['ati:cloud:confluence:space'];
const DEFAULT_LOOM_SPACE_INITIAL_LIMIT = 15;
const DEFAULT_LOOM_SPACE_LOOKUP_LIMIT = 15;

const isLoomSpaceProduct = (product?: string) =>
	product === ProductKeys.Loom && fg('loom-rovo-search');

const resolveSpaceFilterConfig = (config: CloudConfig): CloudConfig => {
	if (
		config.product ||
		config.experience !== SearchExperience.QUICK_FIND ||
		!isLoomSpaceProduct(config.primaryProduct)
	) {
		return config;
	}

	return { ...config, product: ProductKeys.Loom };
};

const fetchRecommendedSpaceOptionsForSite = async (
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const recommendedSpacesResponse = await smartsRecommendedContainerQuery({
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		variables: {
			userId: config.userId || '',
			tenantId: cloudId,
		},
		headers,
	});

	const allRecommendedSpaces: Space[] =
		recommendedSpacesResponse.data?.smarts.results?.flatMap(({ container }) => {
			if (!(container && container.key)) {
				return [];
			}
			return [
				{
					key: container.key,
					name: container.name,
					ari: container.id,
					avatarUrl:
						container.links?.base && container.icon?.path
							? `${container.links.base}${container.icon.path}`
							: '',
				},
			];
		}) || [];

	return allRecommendedSpaces.map((space) => ({
		trackingKey: space.ari,
		value: space.key,
		queryParamValue: space.key,
		avatarUrl: space.avatarUrl,
		label: space.name || '',
		product: config.product,
	}));
};

const isSingleSelectSpaceProduct = (product?: string) =>
	is3pProductKey(product ?? '') && !isLoomSpaceProduct(product);

async function searchDcSpaces(query: string, config: CloudConfig) {
	const { useCaseId } = config;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	let spacesResponse = await confluenceDcSpaceFilterOptionsQuery({
		variables: {
			query,
			useCaseId,
			cloudIdARI: `ari:cloud:confluence::site/${config.cloudId}`,
			limit: 5,
			entities: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
			product: ProductKeys.ConfluenceDC,
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		headers,
	});

	return (
		spacesResponse?.data?.search?.search?.edges?.map(({ node }) => ({
			trackingKey: node.entity.id,
			value: node.entity.id,
			queryParamValue: node.entity.id,
			avatarUrl: node.entity?.icon?.url,
			label: cleanExcerpt(node.entity.displayName),
			product: config.product,
		})) ?? []
	);
}

async function searchThirdPartySpaceOptions(config: CloudConfig, query: string = '') {
	const cloudId = config.cloudId;
	const product = config.product;
	const { useCaseId } = config;

	// Get integrationAri from the static ThirdPartyConfigs using the product key.
	// This avoids the dependency on selectedDatasource being populated in the store,
	// which was causing lookup/search to fail when selectedDatasource was undefined.
	const integrationAri =
		config.selectedDatasource?.integrationAri ??
		ThirdPartyConfigs.get(product as ProductKeys3P)?.integrationAri;

	if (!cloudId || !product || !integrationAri) {
		return [];
	}

	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	const subtypes =
		product === ProductKeys.Sharepoint
			? [NounSubtypeKey.Site, NounSubtypeKey.Subsite]
			: [NounSubtypeKey.Space];

	let spacesResponse = await thirdPartySpaceFilterOptionsQuery({
		variables: {
			query,
			useCaseId,
			cloudIdARI: [`ari:cloud:confluence::site/${cloudId}`],
			limit: 15,
			entities: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
			product,
			integration: integrationAri,
			subtypes,
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		headers,
	});

	return (
		spacesResponse?.data?.search?.search?.edges?.map(({ node }) => ({
			value: node.entity.id,
			trackingKey: node.entity.id,
			queryParamValue: node.entity.id,
			avatarUrl: node.entity?.icon?.url,
			label: cleanExcerpt(node.entity.displayName),
			product,
		})) ?? []
	);
}

async function fetchLoomSpaceOptions(
	config: CloudConfig,
	{ query = '', limit = DEFAULT_LOOM_SPACE_INITIAL_LIMIT }: { query?: string; limit?: number } = {},
) {
	const cloudId = config.cloudId;
	if (!cloudId) {
		return [];
	}

	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	const spacesResponse = await loomSearchEntitiesQuery({
		variables: {
			query: query || undefined,
			siteId: cloudId,
			limit,
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		headers,
	});

	return (
		spacesResponse?.data?.loom_spacesSearch?.map(
			(space: LoomSpacesSearchQueryData['loom_spacesSearch'][number]) => ({
				value: space.id,
				trackingKey: space.id,
				queryParamValue: space.id,
				avatarUrl: undefined,
				label: cleanExcerpt(space.name),
				product: ProductKeys.Loom,
			}),
		) ?? []
	);
}

const spaceFilterInitial: CreateSelectFilter<'space'> = {
	id: 'space',
	products: getFilterProducts(),
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'spaces',
		fetcher: async (queryParams, config) => {
			if (isLoomSpaceProduct(config.product)) {
				const headers = buildAtlAttributionHeaders({
					cloudId: config.cloudId,
					activationId: config.activationId,
					product: config.primaryProduct,
				});
				const spacesResponse = await loomSpaceARIEntitiesQuery({
					variables: {
						ids: queryParams,
					},
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				const spacesOptions = spacesResponse?.data?.loom_spaces?.map(
					(space: LoomSpacesByIdsQueryData['loom_spaces'][number]) => ({
						trackingKey: space.id,
						value: space.id,
						label: space.name,
						avatarUrl: undefined,
						queryParamValue: space.id,
						product: ProductKeys.Loom,
					}),
				);

				return spacesOptions || [];
			}

			if (
				config.product === ProductKeys.ConfluenceDC ||
				config.product === ProductKeys.GoogleDrive ||
				config.product === ProductKeys.Sharepoint
			) {
				const headers = buildAtlAttributionHeaders({
					cloudId: config.cloudId,
					activationId: config.activationId,
					product: config.primaryProduct,
				});
				const spacesResponse = await spaceARIEntitiesQuery({
					variables: {
						ids: queryParams,
					},
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				});

				const spacesOptions = spacesResponse.data?.external_entities?.space?.map((space) => ({
					trackingKey: space.id,
					value: space.id,
					label: space.displayName,
					avatarUrl: space.icon?.url || '',
					queryParamValue: space.id,
					product: config.product,
				}));

				return spacesOptions || [];
			}

			const cloudId = config.cloudId;

			// Practically cloudId should always be present, but we need to satisfy the type checker
			if (!cloudId) {
				return [];
			}
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const response = await confluenceSpaceKeysToIdsQuery({
				spaceKeys: queryParams,
				cloudId,
				headers,
			});
			const spaceIds = response.data?.spaces?.nodes?.map((node) => node?.id).filter(notEmpty) ?? [];
			const spaceARIs = spaceIds.map((id) => `ari:cloud:confluence:${cloudId}:space/${id}`);

			const initialSpacesResponse = await confluenceSpacesQuery(spaceARIs, headers);

			const allRecommendedSpaces: Space[] =
				initialSpacesResponse.data?.confluence.spaces.map((space) => ({
					key: space.key,
					name: space.name,
					ari: space.id,
					avatarUrl:
						space.links?.base && space.icon?.path ? `${space.links.base}${space.icon.path}` : '',
				})) || [];

			return allRecommendedSpaces.map((space) => ({
				trackingKey: space.ari,
				value: space.key,
				queryParamValue: space.key,
				avatarUrl: space.avatarUrl,
				label: space.name || '',
				product: config.product,
			}));
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			if (isLoomSpaceProduct(config.product)) {
				return fetchLoomSpaceOptions(config, { limit: DEFAULT_LOOM_SPACE_INITIAL_LIMIT });
			}

			if (config.product === ProductKeys.ConfluenceDC) {
				const spaces = await searchDcSpaces('', config);
				return spaces;
			}
			if (config.product === ProductKeys.GoogleDrive || config.product === ProductKeys.Sharepoint) {
				return searchThirdPartySpaceOptions(config);
			}

			return fetchOptionsAcrossSites(config, (cloudId) =>
				fetchRecommendedSpaceOptionsForSite(config, cloudId),
			);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			if (isLoomSpaceProduct(config.product)) {
				return fetchLoomSpaceOptions(config, {
					query,
					limit: DEFAULT_LOOM_SPACE_LOOKUP_LIMIT,
				});
			}

			if (config.product === ProductKeys.ConfluenceDC) {
				return searchDcSpaces(query, config);
			}
			if (config.product === ProductKeys.GoogleDrive || config.product === ProductKeys.Sharepoint) {
				return searchThirdPartySpaceOptions(config, query);
			}

			const backendExperiment =
				config.experimentConfig &&
				getBackendExperiment({
					experimentConfig: config.experimentConfig,
					entities: LOOKUP_ENTITIES,
					productEdition: config.productEdition,
					isHello: isHello(config.cloudId),
					productDataRegions: config.productDataRegions,
					experience: 'search-page-space-filter',
					tenantId: config.cloudId,
				});

			const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const [currentCloudId = '', ...additionalCloudIds] = config.selectedCloudIds ?? [];
			const { useCaseId } = config;
			const response = await searchEntitiesQuery({
				aggAbsoluteUrl: config.aggAbsoluteUrl,
				variables: {
					query,
					useCaseId,
					first: 15,
					filters: {
						entities: ['ati:cloud:confluence:space'],
						// get ARIs for each cloud id
						locations: getCloudIdARIs(currentCloudId, config.primaryProduct, additionalCloudIds),
					},
					experience: 'full-page-search-space-filter',
					experimentId,
					shadowExperimentId,
					experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
						name,
						layerId,
						shadowId,
						definitions,
					})),
				},
				includeSpaceKey: true,
				headers,
			});
			const { data } = response;
			if (!data) {
				return [];
			}

			const lookupOptions =
				data.search?.results.edges.map(({ node }) => ({
					trackingKey: node.id || '',
					value: node.key || '',
					queryParamValue: node.key ?? '',
					avatarUrl: node.iconUrl || undefined,
					label: node.title,
					product: config.product,
				})) ?? [];

			// A space key can repeat across sites (personal spaces share `~<aaid>`), so collapse them
			// into a single option.
			return mergeAndDedupeOptions([lookupOptions]);
		},
	},
	isSuggestedFilter: (primaryProduct) => primaryProduct === ProductKeys.Confluence,
};

const getSpaceFilterInitial = (): CreateSelectFilter<'space'> => ({
	...spaceFilterInitial,
	products: getFilterProducts(),
});

/**
 * Builds the raw (unwrapped) monad for the given state and config, choosing
 * SingleSelectFilter for 3P products and MultiSelectFilter for 1P. Used internally
 * so that withSelectTypeFilter can wrap exactly once — calling filter.withState would
 * double-wrap because filter.withState itself calls withSelectTypeFilter for 3P.
 */
const buildSelectFilter = (
	state: SelectFilterState<'space'>,
	config: CloudConfig,
): SelectFilterMonad<'space'> => {
	if (isSingleSelectSpaceProduct(config.product)) {
		return SingleSelectFilter({
			initial: getSpaceFilterInitial(),
			state: { ...state, type: 'single-select' },
			config,
		}) as SelectFilterMonad<'space'>;
	}
	return MultiSelectFilter({
		initial: getSpaceFilterInitial(),
		state: { ...state, type: 'multi-select' },
		config,
	}) as SelectFilterMonad<'space'>;
};

/**
 * Ensures every chained monad operation re-dispatches through buildSelectFilter so the
 * correct select type (SingleSelectFilter for 3P, MultiSelectFilter for 1P) is preserved
 * throughout the chain. Without this, base-select-filter operations (loadInitialOptions,
 * setQueryParams, etc.) return a plain SelectFilter monad that always uses multi-select
 * setOption. buildSelectFilter is used instead of filter.withState to avoid double-wrapping:
 * filter.withState for 3P already calls withSelectTypeFilter internally.
 */
const withSelectTypeFilter = (
	spaceFilter: SelectFilterMonad<'space'>,
	config: CloudConfig,
): SelectFilterMonad<'space'> => ({
	...spaceFilter,
	loadInitialOptions: async (onStateChange) =>
		withSelectTypeFilter(
			buildSelectFilter((await spaceFilter.loadInitialOptions(onStateChange)).getState(), config),
			config,
		),
	setOption: (option, customValue) =>
		withSelectTypeFilter(
			buildSelectFilter(spaceFilter.setOption(option, customValue).getState(), config),
			config,
		),
	clearOptions: () =>
		withSelectTypeFilter(buildSelectFilter(spaceFilter.clearOptions().getState(), config), config),
	setLookupInput: async (query, onStateChange) =>
		withSelectTypeFilter(
			buildSelectFilter(
				(await spaceFilter.setLookupInput(query, onStateChange)).getState(),
				config,
			),
			config,
		),
	clear: () =>
		withSelectTypeFilter(buildSelectFilter(spaceFilter.clear().getState(), config), config),
	queryParams: {
		...spaceFilter.queryParams,
		set: async (queryParams, onStateChange) =>
			withSelectTypeFilter(
				buildSelectFilter(
					(await spaceFilter.queryParams.set(queryParams, onStateChange)).getState(),
					config,
				),
				config,
			),
	},
});

// Used only for its static shape (id, type, products, queryParams.key, etc.) via the
// spread below. withState and getInitialState are overridden to dispatch per product.
const baseFilter = createMultiSelectFilter(spaceFilterInitial);

export const filter: Readonly<{
	id: 'space';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'space'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'space'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'space'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = {
	...baseFilter,
	queryParams: {
		...baseFilter.queryParams,
		getState: (queryParams, cloudId) => ({
			...baseFilter.queryParams.getState(queryParams, cloudId),
			products: getFilterProducts(),
		}),
	},
	withState: (state, config): SelectFilterMonad<'space'> => {
		const resolvedConfig = resolveSpaceFilterConfig(config);
		// buildSelectFilter is the single source of truth for monad construction.
		// For 3P, wrap with withSelectTypeFilter to preserve single-select dispatch
		// through every chained operation; 1P uses MultiSelectFilter directly.
		const monad = buildSelectFilter(
			{
				...(state as SelectFilterState<'space'>),
				products: getFilterProducts(),
			},
			resolvedConfig,
		);
		return isSingleSelectSpaceProduct(resolvedConfig.product)
			? withSelectTypeFilter(monad, resolvedConfig)
			: monad;
	},
	getInitialState: (config) => {
		const resolvedConfig = resolveSpaceFilterConfig(config);
		const currentBaseFilter = createMultiSelectFilter(getSpaceFilterInitial());
		return filter.withState(
			currentBaseFilter.getInitialState(resolvedConfig).getState(),
			resolvedConfig,
		);
	},
	get products() {
		return getFilterProducts();
	},
};
